
/**
 * @public
 */
export enum SPResourcePathFormat {
    absolute,
    relative,
    serverRelative
}

/**
 * Simplified implementation of a URI parser which does not attempt decoding of segments,
 * handling of query strings, or separation of the fragment. Intended for use on SharePoint
 * Resource Paths or other URL-like entities which may not actually conform to the URL
 * specification, but may be used for simple transformations of URLs.
 *
 * This class makes several assumptions about the format of input URLs:
 * - Absolute URLs begin with '<scheme>://<host>'
 * - Relative URLs being with '/'
 * - URLs never have a trailing '/'
 *
 * @public
 */
export class SPResourcePath {
    /**
     * Determines the format of the URL.
     *
     * @readonly
     *
     * @example
     *  expect(new SPResourcePath('/').format).to.equal(SPResourcePathFormat.serverRelative);
     * @example
     *  expect(new SPResourcePath('https://test').format).to.equal(SPResourcePathFormat.absolute);
     * @example
     *  expact(new SPResourcePath('test').format).to.equal(SPResourcePathFormat.relative);
     */
    public readonly format: SPResourcePathFormat;

    /**
     * Gets the authority component of the URI, if the URI is fully-qualified.
     * The authority is formatted as
     * '<scheme>://<host>'
     * and never has a trailing '/'.
     *
     * @readonly
     *
     * @example
     *  expect(new SPResourcePath('/').authority).to.equal('');
     * @example
     *  expect(new SPResourcePath('https://test').authority).to.equal('https://test');
     * @example
     *  expect(new SPResourcePath('https://test/').authority).to.equal('https://test');
     * @example
     *  expect(new SPResourcePath('/bad/scheme://').authority).to.equal('');
     */
    public readonly authority: string;

    /**
     * Gets the domain of the URI, which is everything in the authority after
     * the scheme and protocol.
     *
     * @readonly
     *
     * @example
     *  expect(new SPResourcePath('/').domain).to.equal('');
     * @example
     *  expect(new SPResourcePath('https://test').domain).to.equal('test');
     * @example
     *  expect(new SPResourcePath('https://test/').domain).to.equal('test');
     */
    public readonly domain: string;

    /**
     * Gets the path of the URI, which is everything after the authority.
     * If the URI has no authority, this returns the entire URI.
     * If there is only an authority, this returns ''.
     *
     * @readonly
     *
     * @example
     *  expect(new SPResourcePath('https://test').path).to.equal('');
     * @example
     *  expect(new SPResourcePath('/').path).to.equal('/');
     * @example
     *  expect(new SPResourcePath('https://test/').path).to.equal('/');
     * @example
     *  expect(new SPResourcePath('/bad/scheme://').path).to.equal('/bad/scheme://');
     */
    public readonly path: string;

    /**
     * The value that passed into SPResourcePath constructor.
     *
     * @readonly
     *
     * @example
     *  expect(new SPResourcePath('https://test').value).to.equal('https://test');
     * @example
     *  expect(new SPResourcePath(undefined).value).to.equal('');
     */
    public readonly value: string;

    /**
     * Gets the segments of the path.
     * If there is no path, this is an empty array.
     * Otherwise, this returns all segments after the first '/'.
     *
     * @readonly
     *
     * @example
     *  expect(new SPResourcePath('https://test').segments).to.deep.equal([]);
     * @example
     *  expect(new SPResourcePath('/').segments).to.deep.equal(['']);
     * @example
     *  expect(new SPResourcePath('/foo').segments).to.deep.equal(['foo']);
     * @example
     *  expect(new SPResourcePath('/foo/').segments).to.deep.equal(['foo', '']);
     * @example
     *  expect(new SPResourcePath('/foo/bar').segments).to.deep.equal(['foo', 'bar']);
     * @example
     *  expect(new SPResourcePath('/bad/scheme://').segments).to.deep.equal(['bad', 'scheme:', '', '']);
     */
    public readonly segments: string[];

    /**
     * Creates an instance of SPResourcePath.
     *
     * @param value - A string representing a well-formed, decoded SharePoint resource path.
     */
    constructor(value: string = '') {
        const rootDelimeter = '//';
        const indexOfRootDelimeter = value.indexOf(rootDelimeter);
        const indexOfPathDelimeter = value.indexOf('/');

        // The root delimeter is the first instance of '//', unless preceded by a lone '/'
        const endIndexOfRootDelimeter =
            indexOfRootDelimeter > -1 && indexOfRootDelimeter <= indexOfPathDelimeter ?
                indexOfRootDelimeter + rootDelimeter.length :
                -1;

        const authority = getAuthority(value, endIndexOfRootDelimeter);

        const domain = authority && authority.slice(endIndexOfRootDelimeter);

        // By definition, everything after the authority is the path
        const path = value.slice(authority.length);

        const format = authority ?
            SPResourcePathFormat.absolute :
            path[0] === '/' ?
                SPResourcePathFormat.serverRelative :
                SPResourcePathFormat.relative;

        const segments = path.split('/');

        this.authority = authority;
        this.domain = domain;
        this.format = format;
        this.path = path;
        this.segments = segments;
        this.value = value;
    }
}

function getAuthority(value: string, endIndexOfRootDelimeter: number): string {
    if (endIndexOfRootDelimeter > -1) {
        const indexOfNextSegment = value.indexOf('/', endIndexOfRootDelimeter);

        if (indexOfNextSegment > -1) {
            return value.slice(0, indexOfNextSegment);
        } else {
            return value;
        }
    } else {
        return '';
    }
}
